import { authMethodFromProvider } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import {
  Button,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
  Input,
} from '@parcel/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { OAUTH_INTENT_KEY, storeSignupRef, takeSignupRef, withRef } from '../../lib/analytics.js';
import { useAuth } from '../../lib/auth.js';
import { GoogleIcon, MicrosoftIcon } from './provider-icons.js';

interface SignupFormProps {
  mode: 'signup' | 'signin';
}

const modeContent = {
  signup: {
    heading: 'Start with Parcel',
    description: "We'll email you a 6-digit code to create your account.",
    submit: 'Email me a code',
    alternatePrompt: 'Already have an account?',
    alternateText: 'Sign in',
  },
  signin: {
    heading: 'Sign in to Parcel',
    description: "We'll email you a 6-digit sign-in code.",
    submit: 'Email me a code',
    alternatePrompt: 'New to Parcel?',
    alternateText: 'Create an account',
  },
};

export function getAuthModeHref(mode: SignupFormProps['mode'], redirect: string | null): string {
  const href = mode === 'signin' ? '/signup' : '/signin';
  return redirect ? `${href}?redirect=${encodeURIComponent(redirect)}` : href;
}

export function sanitizeRedirectPath(redirect: string | null): string {
  if (redirect?.startsWith('/') && !redirect.startsWith('//')) return redirect;
  return '/';
}

export function AuthProviderButtons({
  onSelect,
  disabled,
}: {
  onSelect: (provider: 'azure' | 'google') => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={disabled}
        onClick={() => onSelect('azure')}
      >
        <MicrosoftIcon />
        Continue with Microsoft
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={disabled}
        onClick={() => onSelect('google')}
      >
        <GoogleIcon />
        Continue with Google
      </Button>
    </div>
  );
}

export function SignupForm({ mode }: SignupFormProps) {
  const {
    devAuthAvailable,
    session,
    signInLocally,
    signInWithEmail,
    signInWithProvider,
    verifyEmailCode,
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const content = modeContent[mode];
  const redirect =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('redirect');
  // Read once: the URL's ref goes to the tab, and the same ref rides every signup event from here.
  const [ref] = useState(() =>
    typeof window === 'undefined' ? null : storeSignupRef(window.location.search),
  );
  const alternateHref = getAuthModeHref(mode, redirect);

  useEffect(() => {
    if (session) void navigate({ to: sanitizeRedirectPath(redirect) });
  }, [navigate, session, redirect]);

  return (
    <div className="w-full max-w-sm space-y-6">
      <div className="space-y-2 text-center">
        <a href="/" className="inline-flex">
          <img
            src="/brand/parcel-logo-black.svg"
            alt="Parcel"
            width="84"
            height="24"
            decoding="async"
            className="dark:hidden"
          />
          <img
            src="/brand/parcel-logo-white.svg"
            alt="Parcel"
            width="84"
            height="24"
            decoding="async"
            className="hidden dark:block"
          />
        </a>
        <div className="space-y-1">
          <h1 className="font-semibold text-2xl tracking-normal">{content.heading}</h1>
          <p className="text-muted-foreground text-sm">{content.description}</p>
        </div>
      </div>

      <div className="rounded-lg border bg-background p-6 shadow-sm">
        {step === 'email' ? (
          <>
            <AuthProviderButtons
              disabled={submitting}
              onSelect={(provider) => {
                setError(null);
                const method = authMethodFromProvider(provider);
                capture('signup_started', withRef({ method, mode }, ref));
                // The redirect destroys this page, so completion has to be
                // claimed on the way back. sessionStorage is per-tab and
                // survives a cross-origin round trip in the same tab, which is
                // exactly the lifetime of one OAuth attempt. Only the mode is
                // stored; the method is read back from the session's own
                // provider, which is ground truth if someone abandons one
                // provider and completes with another.
                window.sessionStorage.setItem(OAUTH_INTENT_KEY, mode);
                void signInWithProvider(
                  provider,
                  redirect ? { redirectTo: redirect } : undefined,
                ).then(({ error }) => {
                  if (error) {
                    window.sessionStorage.removeItem(OAUTH_INTENT_KEY);
                    capture('signup_failed', { method, reason: error.message });
                    setError(error.message);
                  }
                });
              }}
            />
            <FieldSeparator className="my-6">Or continue with email</FieldSeparator>
          </>
        ) : null}
        {step === 'code' ? (
          <form
            className="space-y-6"
            onSubmit={async (event) => {
              event.preventDefault();
              setError(null);
              setSubmitting(true);
              const { error } = await verifyEmailCode(email, code.trim());
              setSubmitting(false);
              if (error) {
                capture('signup_failed', { method: 'email_otp', reason: error.message });
                setError(error.message);
                return;
              }
              capture('signup_completed', withRef({ method: 'email_otp', mode }, takeSignupRef()));
            }}
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="auth-code">6-digit code</FieldLabel>
                <Input
                  id="auth-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                />
                <FieldDescription>We sent a code to {email}.</FieldDescription>
              </Field>

              {error ? <p className="text-sm text-destructive">{error}</p> : null}

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || code.trim().length < 6}
              >
                {submitting ? 'Verifying...' : 'Verify code'}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError(null);
                  }}
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
                  disabled={submitting}
                  onClick={async () => {
                    setError(null);
                    const { error } = await signInWithEmail(
                      email,
                      redirect ? { redirectTo: redirect } : undefined,
                    );
                    if (error) {
                      capture('signup_failed', { method: 'email_otp', reason: error.message });
                      setError(error.message);
                      return;
                    }
                    capture('signup_otp_sent', { mode });
                  }}
                >
                  Resend code
                </button>
              </div>
            </FieldGroup>
          </form>
        ) : (
          <form
            className="space-y-6"
            onSubmit={async (event) => {
              event.preventDefault();
              setError(null);
              capture('signup_started', withRef({ method: 'email_otp', mode }, ref));
              setSubmitting(true);
              const { error } = await signInWithEmail(
                email,
                redirect ? { redirectTo: redirect } : undefined,
              );
              setSubmitting(false);

              if (error) {
                capture('signup_failed', { method: 'email_otp', reason: error.message });
                setError(error.message);
                return;
              }

              capture('signup_otp_sent', { mode });
              setStep('code');
            }}
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="auth-email">Email</FieldLabel>
                <Input
                  id="auth-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </Field>

              {error ? <p className="text-sm text-destructive">{error}</p> : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Sending...' : content.submit}
              </Button>
            </FieldGroup>
          </form>
        )}

        {devAuthAvailable ? (
          <div className="mt-6 space-y-4">
            <FieldSeparator>Or</FieldSeparator>
            <Button type="button" variant="outline" className="w-full" onClick={signInLocally}>
              Continue locally
            </Button>
            <FieldDescription>
              Local preview mode uses mock auth and stays in this browser.
            </FieldDescription>
          </div>
        ) : null}
      </div>

      <div className="space-y-4 text-center">
        <p className="text-muted-foreground text-sm">
          {content.alternatePrompt}{' '}
          <a
            href={alternateHref}
            className="font-medium underline underline-offset-4 hover:text-foreground"
          >
            {content.alternateText}
          </a>
        </p>
        <p className="px-4 text-muted-foreground text-xs">
          By continuing, you agree to our{' '}
          <a
            href="https://docs.google.com/document/d/1nsNYg5tY_3ovEfsFhBQhiejCYCciITEXnyecwDbusDU/edit?usp=sharing"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://docs.google.com/document/d/1BDVP1oJqmksIFjD698A57S8BGFdg5VyyOdmKksimrSM/edit?usp=sharing"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
